import { chartColors, scoreColor } from "@/lib/chart-colors";
import { useState, useEffect, useRef } from "react";
import { Plus, Calendar, GripVertical, Filter, Users, TrendingUp, FolderKanban, Edit, Trash2, Bot, CheckCircle, XCircle, Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useDroppable,
} from '@dnd-kit/core';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import {
  FormDealModal,
  AlertDialogDelete
} from "@/components/common";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useFetchDealsQuery, useFetchStagesQuery, useMoveDealStageMutation, useCreateStageMutation, useUpdateDealMutation } from "@/hooks";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";

interface DealCardProps {
  deal: any;
  isOverlay?: boolean;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

function DealCard({ deal, isOverlay = false }: DealCardProps) {
  const updateDealMutation = useUpdateDealMutation();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: deal.id, attributes: {
      roleDescription: 'Deal card',
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      open: { label: "Aberto", bg: "bg-app-info/10", text: "text-app-info", border: "border-app-info/30" },
      won: { label: "Ganho", bg: "bg-app-success/10", text: "text-app-success", border: "border-app-success/30" },
      lost: { label: "Perdido", bg: "bg-app-danger/10", text: "text-app-danger", border: "border-app-danger/30" },
    };

    const config = statusConfig[status as keyof typeof statusConfig];
    return config ? (
      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text} border ${config.border}`}>
        {config.label}
      </span>
    ) : null;
  };

  const getProbabilityColor = (probability: number) => {
    return scoreColor(probability);
  };

  const handleQuickStatus = (e: React.MouseEvent, status: 'won' | 'lost') => {
    e.stopPropagation();
    updateDealMutation.mutate({ id: deal.id, data: { status } });
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={` from-white via-white border border-app-border/60 rounded-2xl p-4 shadow-lg hover:shadow-2xl  transition-all duration-300 cursor-move group ${isOverlay ? 'rotate-2 shadow-xl scale-105' : ''}`}
    >
      <div {...attributes}
        {...listeners}
        className="group-hover:bg-brand-soft/50 group-hover:to-transparent -mx-4 -my-3 px-4 py-3 rounded-t-2xl transition-all duration-300"
      >

        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {!isOverlay && <GripVertical className="h-4 w-4 text-app-fg-subtle group-hover:text-brand transition-colors duration-300" />}
            <h3 className="font-semibold text-app-fg text-sm">{deal.title}</h3>
          </div>
          {getStatusBadge(deal.status)}
        </div>

        <div className="space-y-2 text-xs">
          {deal.lead?.source === 'Instagram AI Listener' && (
            <div className="flex items-center gap-1.5 text-brand bg-brand-soft px-2 py-1 rounded-md w-max font-medium shadow-sm mb-2 border border-brand-soft">
              <Bot size={14} />
              Captado via IA (Instagram)
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-app-fg-muted">Lead:</span>
            <span className="font-medium text-app-fg-muted truncate">{deal.lead?.name}</span>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-app-fg-muted">Valor:</span>
            <span className="font-semibold text-brand">{formatCurrency(deal.value)}</span>
          </div>

          <div className="flex items-center gap-2">
            <Calendar className="h-3 w-3 text-app-fg-subtle" />
            <span className="text-app-fg-muted">
              {new Date(deal.created_at).toLocaleDateString('pt-BR')}
            </span>
          </div>

          {/* Barra de probabilidade */}
          {deal.probability != null && deal.probability > 0 && (
            <div className="mt-1">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-app-fg-subtle">Probabilidade</span>
                <span className="font-medium text-app-fg-muted">{deal.probability}%</span>
              </div>
              <div className="w-full bg-app-border rounded-full h-1.5">
                <div
                  className="h-1.5 rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.min(deal.probability, 100)}%`,
                    backgroundColor: getProbabilityColor(deal.probability),
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {!isOverlay && (
        <div className="flex items-center justify-between gap-1 mt-3 pt-3 border-t border-app-border-subtle">
          <div className="flex items-center gap-1">
            <FormDealModal deal={deal}>
              <Button variant="ghost" size="sm" className="h-10 w-10 md:h-8 md:w-8 p-0 text-app-fg-muted hover:text-brand hover:bg-brand-soft transition-all duration-200" onClick={(e) => e.stopPropagation()}>
                <Edit className="h-5 w-5 md:h-4 md:w-4" />
              </Button>
            </FormDealModal>

            {/* Quick actions: Ganho / Perdido */}
            {deal.status === 'open' && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-10 w-10 md:h-8 md:w-8 p-0 text-app-fg-muted hover:text-app-success hover:bg-app-success/10 transition-all duration-200"
                  onClick={(e) => handleQuickStatus(e, 'won')}
                  title="Marcar como Ganho"
                  disabled={updateDealMutation.isLoading}
                >
                  <CheckCircle className="h-5 w-5 md:h-4 md:w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-10 w-10 md:h-8 md:w-8 p-0 text-app-fg-muted hover:text-app-danger hover:bg-app-danger/10 transition-all duration-200"
                  onClick={(e) => handleQuickStatus(e, 'lost')}
                  title="Marcar como Perdido"
                  disabled={updateDealMutation.isLoading}
                >
                  <XCircle className="h-5 w-5 md:h-4 md:w-4" />
                </Button>
              </>
            )}
          </div>

          <AlertDialogDelete
            endpoint={`/clinic/crm/deals/${deal.id}`}
            queryKey="deals"
          >
            <Button variant="ghost" size="sm" className="h-10 w-10 md:h-8 md:w-8 p-0 text-app-fg-muted hover:text-app-danger hover:bg-app-danger/10 transition-all duration-200" onClick={(e) => e.stopPropagation()}>
              <Trash2 className="h-5 w-5 md:h-4 md:w-4" />
            </Button>
          </AlertDialogDelete>
        </div>
      )}
    </div>
  );
}

interface KanbanColumnProps {
  stage: any;
  deals: any[];
}

function KanbanColumn({ stage, deals }: KanbanColumnProps) {
  const { setNodeRef } = useDroppable({
    id: stage.id,
  });

  const totalValue = deals.reduce((sum, deal) => sum + Number(deal.value || 0), 0);

  return (
    <div className="flex-shrink-0 w-80">
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <div
            className="w-3 h-3 rounded"
            style={{ backgroundColor: stage.color || chartColors.brand }}
          />
          <h3 className="font-semibold text-app-fg-strong">{stage.name}</h3>
          <span className="text-sm text-app-fg-muted">({deals.length})</span>
        </div>
        {totalValue > 0 && (
          <span className="text-xs font-medium text-brand ml-5">
            {formatCurrency(totalValue)}
          </span>
        )}
      </div>

      <div
        ref={setNodeRef}
        className="bg-app-surface-muted rounded-lg p-3 min-h-[400px]"
      >
        <SortableContext items={deals.map(deal => deal.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {deals.map((deal) => (
              <DealCard key={deal.id} deal={deal} />
            ))}
          </div>
        </SortableContext>
      </div>
    </div>
  );
}

// Componente para adicionar nova etapa inline (estilo Trello)
function AddStageColumn({ stages }: { stages: any[] }) {
  const [isAdding, setIsAdding] = useState(false);
  const [stageName, setStageName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const createStageMutation = useCreateStageMutation();

  useEffect(() => {
    if (isAdding && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAdding]);

  const handleSubmit = () => {
    if (!stageName.trim()) return;

    const maxOrder = stages.length > 0
      ? Math.max(...stages.map(s => s.order ?? 0))
      : -5;

    createStageMutation.mutate(
      { name: stageName.trim(), order: maxOrder + 5, color: 'hsl(32 6% 51%)' },
      {
        onSuccess: () => {
          setStageName("");
          setIsAdding(false);
        },
      }
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSubmit();
    if (e.key === 'Escape') {
      setStageName("");
      setIsAdding(false);
    }
  };

  if (!isAdding) {
    return (
      <div className="flex-shrink-0 w-80">
        <Button
          variant="ghost"
          className="w-full h-12 border-2 border-dashed border-app-border rounded-lg text-app-fg-muted hover:text-brand hover:border-brand hover:bg-brand-soft/50 transition-all duration-200"
          onClick={() => setIsAdding(true)}
        >
          <Plus className="mr-2 h-4 w-4" />
          Adicionar Etapa
        </Button>
      </div>
    );
  }

  return (
    <div className="flex-shrink-0 w-80">
      <div className="bg-white rounded-lg p-3 border border-app-border shadow-sm space-y-2">
        <Input
          ref={inputRef}
          placeholder="Nome da etapa..."
          value={stageName}
          onChange={(e) => setStageName(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={createStageMutation.isLoading}
        />
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="bg-brand hover:bg-brand-hover text-brand-fg"
            onClick={handleSubmit}
            disabled={!stageName.trim() || createStageMutation.isLoading}
          >
            {createStageMutation.isLoading ? "Criando..." : "Adicionar"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setStageName(""); setIsAdding(false); }}
            disabled={createStageMutation.isLoading}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// Componente de filtros do Kanban
interface KanbanFiltersState {
  search?: string;
  status?: "open" | "won" | "lost";
  start_date?: string;
  end_date?: string;
}

function KanbanFilters({
  filters,
  onFiltersChange,
}: {
  filters: KanbanFiltersState;
  onFiltersChange: (filters: KanbanFiltersState) => void;
}) {
  const [searchInput, setSearchInput] = useState(filters.search || "");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onFiltersChange({ ...filters, search: value || undefined });
    }, 300);
  };

  const handleClear = () => {
    setSearchInput("");
    onFiltersChange({});
  };

  const hasFilters = filters.search || filters.status || filters.start_date || filters.end_date;

  return (
    <div className="flex flex-wrap items-center gap-3 p-3 bg-white border border-app-border rounded-lg">
      <div className="relative flex-1 min-w-[200px] max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-app-fg-subtle" />
        <Input
          placeholder="Buscar oportunidade..."
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="pl-9 h-9"
        />
      </div>

      <Select
        value={filters.status || "all"}
        onValueChange={(value) =>
          onFiltersChange({ ...filters, status: value === "all" ? undefined : value as "open" | "won" | "lost" })
        }
      >
        <SelectTrigger className="w-[140px] h-9">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todos</SelectItem>
          <SelectItem value="open">Aberto</SelectItem>
          <SelectItem value="won">Ganho</SelectItem>
          <SelectItem value="lost">Perdido</SelectItem>
        </SelectContent>
      </Select>

      <div className="flex items-center gap-2">
        <Input
          type="date"
          value={filters.start_date || ""}
          onChange={(e) => onFiltersChange({ ...filters, start_date: e.target.value || undefined })}
          className="h-9 w-[150px]"
          placeholder="Data início"
        />
        <span className="text-app-fg-subtle text-sm">até</span>
        <Input
          type="date"
          value={filters.end_date || ""}
          onChange={(e) => onFiltersChange({ ...filters, end_date: e.target.value || undefined })}
          className="h-9 w-[150px]"
          placeholder="Data fim"
        />
      </div>

      {hasFilters && (
        <Button variant="ghost" size="sm" onClick={handleClear} className="h-9 text-app-fg-muted hover:text-app-danger">
          <X className="mr-1 h-4 w-4" />
          Limpar
        </Button>
      )}
    </div>
  );
}

export function Deals() {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeDeal, setActiveDeal] = useState<any>(null);
  const [localDeals, setLocalDeals] = useState<any[]>([]);
  const [originalStageId, setOriginalStageId] = useState<string | null>(null);
  const [filters, setFilters] = useState<KanbanFiltersState>({});

  const {
    data: dealsData,
    isLoading: isLoadingDeals,
  } = useFetchDealsQuery(filters);

  const {
    data: stagesData,
    isLoading: isLoadingStages,
  } = useFetchStagesQuery();

  useEffect(() => {
    if (dealsData) {
      setLocalDeals(dealsData);
    }
  }, [dealsData]);

  const isLoading = isLoadingDeals || isLoadingStages;

  // Organizar dados em formato de pipeline
  const stages = stagesData?.items || [];
  const pipelineData = stages && stages.length > 0 ? {
    columns: stages.map((stage: any) => ({
      stage,
      deals: localDeals?.filter(deal => deal.stage_id === stage.id) || []
    })),
    stages
  } : null;

  const moveDealStageMutation = useMoveDealStageMutation();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id as string);

    // Encontrar o deal ativo e salvar o stage original
    const currentDeal = localDeals?.find(deal => deal.id === active.id);
    setOriginalStageId(currentDeal?.stage_id || null);

    setActiveDeal(currentDeal || null);
  };

  const handleDragOver = (event: any) => {
    const { active, over } = event;

    if (!over) return;

    const activeDealId = active.id as string;
    const overId = over.id as string;

    // Verificar se overId é um stage OU um deal
    let targetStage = stages.find((stage: any) => stage.id === overId);

    if (!targetStage) {
      // Se não é stage, verificar se é um deal
      const overDeal = localDeals.find(deal => deal.id === overId);
      if (overDeal) {
        targetStage = stages.find((stage: any) => stage.id === overDeal.stage_id);
      }
    }

    if (!targetStage) return;

    // Encontrar o deal atual
    const currentDeal = localDeals?.find(deal => deal.id === activeDealId);

    if (!currentDeal) return;

    // Se o deal já está no estágio correto, não precisa mover
    if (currentDeal.stage_id === targetStage.id) return;

    // Atualizar o estado local imediatamente para mover o card visualmente
    setLocalDeals(prev =>
      prev.map(deal =>
        deal.id === activeDealId
          ? { ...deal, stage_id: targetStage.id }
          : deal
      )
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || !active) {
      setActiveId(null);
      setActiveDeal(null);
      setOriginalStageId(null);
      return;
    }

    const activeDealId = active.id as string;
    const overId = over.id as string;

    // Verificar se overId é um stage OU um deal
    let targetStage = stages.find((stage: any) => stage.id === overId);

    if (!targetStage) {
      // Se não é stage, verificar se é um deal
      const overDeal = localDeals.find(deal => deal.id === overId);
      if (overDeal) {
        targetStage = stages.find((stage: any) => stage.id === overDeal.stage_id);
      }
    }

    if (targetStage) {
      // Usar o stage original para verificar se houve mudança
      if (originalStageId && originalStageId !== targetStage.id) {
        // Chamar a API em background - movimento já foi feito no handleDragOver
        moveDealStageMutation.mutate({
          id: activeDealId,
          stageId: targetStage.id,
        }, {
          onError: () => {
            // Se a API falhar, reverter a mudança no estado local
            const previousDeals = dealsData || [];
            setLocalDeals(previousDeals);
          }
        });
      }
    }

    setActiveId(null);
    setActiveDeal(null);
    setOriginalStageId(null);
  };

  const hasStages = stages && stages.length > 0;

  return (
    <section className="space-y-5">
      <div className="flex justify-between items-center">
        <div className="space-y-3">
          <h1 className="text-2xl font-semibold tracking-tight text-app-fg-strong">Oportunidades</h1>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink to="/crm/deals">
                  CRM
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Oportunidades</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        {hasStages && (
          <FormDealModal>
            <Button className="px-10">
              <Plus className="mr-2 h-4 w-4" />
              {t("LABEL.new")}
            </Button>
          </FormDealModal>
        )}
      </div>

      <div className="space-y-3">
        {isLoading ? (
          <Skeleton className="w-full h-[600px]" />
        ) : (
          <div>
            {pipelineData && pipelineData.columns.length > 0 ? (
              <>
                {/* Filtros */}
                <KanbanFilters filters={filters} onFiltersChange={setFilters} />

                <div className="mt-3">
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCorners}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="overflow-x-auto">
                      <div className="flex gap-6 p-4 min-w-max bg-app-surface-muted rounded-lg">
                        {pipelineData.columns.map((column: any) => (
                          <KanbanColumn
                            key={column.stage.id}
                            stage={column.stage}
                            deals={column.deals}
                          />
                        ))}
                        <AddStageColumn stages={stages} />
                      </div>
                    </div>

                    <DragOverlay>
                      {activeId && activeDeal ? (
                        <DealCard deal={activeDeal} isOverlay={true} />
                      ) : null}
                    </DragOverlay>
                  </DndContext>
                </div>
              </>
            ) : (
              <EmptyDealsState
                hasStages={hasStages}
              />
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function EmptyDealsState({ hasStages }: {
  hasStages: boolean,
}) {
  return (
    <div className="max-w-4xl mx-auto">
      {/* Hero Section */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-24 h-24 mb-6 rounded-full bg-app-info/10">
          <FolderKanban className="h-12 w-12 text-app-info" />
        </div>
        <h1 className="text-3xl font-bold text-app-fg-strong mb-4">
          Bem-vindo ao seu Pipeline de Oportunidades
        </h1>
        <p className="text-lg text-app-fg-muted max-w-2xl mx-auto">
          Organize e acompanhe todas as suas oportunidades em um só lugar.
          Comece criando seu primeiro deal para otimizar seu processos.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        <div className="bg-white rounded-xl p-6 border border-app-border shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-app-info/10">
              <TrendingUp className="h-6 w-6 text-app-info" />
            </div>
            <div>
              <h3 className="font-semibold text-app-fg-strong">Oportunidades</h3>
              <p className="text-sm text-app-fg-muted">Acompanhe o progresso de oportunidades</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-app-border shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-app-success/10">
              <Users className="h-6 w-6 text-app-success" />
            </div>
            <div>
              <h3 className="font-semibold text-app-fg-strong">Gestão de Leads</h3>
              <p className="text-sm text-app-fg-muted">Converta leads em oportunidades</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-app-border shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-brand-soft">
              <Filter className="h-6 w-6 text-brand" />
            </div>
            <div>
              <h3 className="font-semibold text-app-fg-strong">Estágios Personalizados</h3>
              <p className="text-sm text-app-fg-muted">Adapte ao seu processo</p>
            </div>
          </div>
        </div>
      </div>

      {/* Empty State Card */}
      <div className="bg-app-info/10 rounded-2xl p-8 border border-app-info/30 mb-8">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
          <div className="lg:w-2/3">
            <h2 className="text-2xl font-bold text-app-fg-strong mb-4">
              {hasStages
                ? "Nenhum deal encontrado no pipeline"
                : "Configure seu pipeline primeiro"}
            </h2>
            <p className="text-app-fg-muted mb-6">
              {hasStages
                ? "Você não tem deals criados ainda. Comece adicionando seu primeiro deal para acompanhar oportunidades."
                : "Antes de criar deals, você precisa configurar os estágios do seu pipeline. Crie estágios como 'Prospecção', 'Negociação', 'Fechado', etc."}
            </p>

            <div className="flex flex-wrap gap-4">
              {hasStages ? (
                <FormDealModal>
                  <Button
                    size="lg"
                    className="px-8 bg-brand hover:bg-brand-hover text-brand-fg shadow-premium-sm hover:shadow-premium-md transition-all"
                  >
                    <Plus className="mr-2 h-5 w-5" />
                    Criar Primeiro Deal
                  </Button>
                </FormDealModal>
              ) : (
                <Button
                  size="lg"
                  className="px-8 bg-brand hover:bg-brand-hover text-brand-fg shadow-premium-sm hover:shadow-premium-md transition-all"
                  asChild
                >
                  <Link to="/crm/stages">
                    <Plus className="mr-2 h-5 w-5" />
                    Configurar Estágios
                  </Link>
                </Button>
              )}
            </div>
          </div>

          <div className="lg:w-1/3 flex justify-center">
            <div className="relative">
              {/* Illustrative pipeline visualization */}
              <div className="w-64 h-48 flex items-center justify-center">
                <div className="relative">
                  {/* Pipeline dots */}
                  <div className="flex items-center justify-between w-48">
                    {["1º", "2º", "3º", "4º"].map((step, index) => (
                      <div key={step} className="flex flex-col items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2
 ${index === 0 ? 'bg-app-info/10 border-2 border-app-info' : 'bg-app-surface-muted border border-app-border'}`}>
                          <span className={`text-sm font-semibold ${index === 0 ? 'text-app-info' : 'text-app-fg-subtle'}`}>
                            {step}
                          </span>
                        </div>
                        <div className="text-xs text-app-fg-muted">Etapa {index + 1}</div>
                      </div>
                    ))}
                  </div>

                  {/* Connecting lines */}
                  <div className="absolute top-5 left-10 w-28 h-0.5 bg-app-info" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Tips */}
      <div className="bg-white rounded-xl p-6 border border-app-border shadow-sm">
        <h3 className="font-semibold text-app-fg-strong mb-4">Como começar:</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3 p-3 hover:bg-app-surface-muted rounded-lg transition-colors">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-app-info/10 flex items-center justify-center">
              <span className="text-app-info font-semibold">1</span>
            </div>
            <div>
              <h4 className="font-medium text-app-fg-strong">Configure seu pipeline</h4>
              <p className="text-sm text-app-fg-muted">Defina os estágios do seu processos</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 hover:bg-app-surface-muted rounded-lg transition-colors">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-app-info/10 flex items-center justify-center">
              <span className="text-app-info font-semibold">2</span>
            </div>
            <div>
              <h4 className="font-medium text-app-fg-strong">Adicione deals</h4>
              <p className="text-sm text-app-fg-muted">Crie oportunidades com valores e prazos</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 hover:bg-app-surface-muted rounded-lg transition-colors">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-app-info/10 flex items-center justify-center">
              <span className="text-app-info font-semibold">3</span>
            </div>
            <div>
              <h4 className="font-medium text-app-fg-strong">Atribua responsáveis</h4>
              <p className="text-sm text-app-fg-muted">Designe membros da equipe para cada deal</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 hover:bg-app-surface-muted rounded-lg transition-colors">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-app-info/10 flex items-center justify-center">
              <span className="text-app-info font-semibold">4</span>
            </div>
            <div>
              <h4 className="font-medium text-app-fg-strong">Acompanhe resultados</h4>
              <p className="text-sm text-app-fg-muted">Monitore métricas e conversões</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
